<!--
  User Menu 组件
  负责显示用户菜单和登录状态
  只消费 props，通过事件上抛操作
-->
<template>
  <div class="user-menu">
    <!-- 用户未登录时显示登录按钮 -->
    <template v-if="!isLoggedIn">
      <el-button type="primary" @click="$emit('show-login')">登录</el-button>
    </template>

    <!-- 用户已登录时显示用户信息 -->
    <template v-else>
      <el-dropdown trigger="hover" :hide-on-click="false">
        <div class="user-info">
          <el-avatar :size="32" :src="userInfo?.avatar || defaultAvatar">
            {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
          </el-avatar>
          <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <div class="modern-user-dropdown">
            <!-- 用户头像和基本信息 -->
            <div class="user-profile-section">
              <div class="user-avatar-wrapper">
                <el-avatar :size="56" :src="userInfo?.avatar || defaultAvatar" class="user-avatar">
                  {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
                </el-avatar>
                <div class="user-status-dot"></div>
              </div>
              <div class="user-basic-info">
                <div class="user-name">{{ userInfo?.username }}</div>
                <div class="user-role-badge">{{ userRoleLabel }}</div>
              </div>
            </div>

            <!-- 配额信息卡片 -->
            <div class="quota-card">
              <div class="quota-header">
                <div class="quota-icon">
                  <el-icon><Star /></el-icon>
                </div>
                <div class="quota-title">今日配额</div>
              </div>
              <div class="quota-content">
                <div class="quota-numbers">
                  <span class="quota-used">{{ quotaUsed }}</span>
                  <span class="quota-separator">/</span>
                  <span class="quota-total">{{ quotaLimit }}</span>
                </div>
                <div class="quota-progress">
                  <div class="quota-progress-bar" :style="{ width: quotaPercentage + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- 快捷操作 -->
            <div class="quick-actions">
              <div class="action-item" @click="$emit('go-to-profile')">
                <div class="action-icon">
                  <el-icon><User /></el-icon>
                </div>
                <span class="action-text">个人中心</span>
              </div>

              <!-- 主题切换 - 暂时注释，后续可能启用 -->
              <!-- <div class="action-item" @click="$emit('theme-toggle')">
                <div class="action-icon">
                  <el-icon>
                    <Sunny v-if="isDarkTheme"/>
                    <Moon v-else/>
                  </el-icon>
                </div>
                <span class="action-text">{{ isDarkTheme ? '浅色模式' : '深色模式' }}</span>
              </div> -->

              <!-- 分割线 -->
              <!-- <div class="menu-divider"></div> -->

              <!-- 帮助文档 -->
              <div class="action-item" @click="$emit('show-guide')">
                <div class="action-icon">
                  <el-icon><QuestionFilled /></el-icon>
                </div>
                <span class="action-text">查看引导</span>
              </div>

              <!-- API 官网 -->
              <div class="action-item" @click="$emit('open-api-site')">
                <div class="action-icon">
                  <el-icon><Link /></el-icon>
                </div>
                <span class="action-text">API 官网</span>
              </div>

              <!-- 分割线 -->
              <div class="menu-divider"></div>

              <div class="action-item logout-action" @click="$emit('logout')">
                <div class="action-icon">
                  <el-icon><SwitchButton /></el-icon>
                </div>
                <span class="action-text">退出登录</span>
              </div>
            </div>
          </div>
        </template>
      </el-dropdown>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  Star,
  User,
  SwitchButton,
  ArrowDown,
  // Sunny,
  // Moon,
  QuestionFilled,
  Link
} from '@element-plus/icons-vue'

interface Props {
  isLoggedIn: boolean
  userInfo?: {
    username: string
    avatar?: string
    role?: string
  }
  quotaUsed: number
  quotaLimit: number
  defaultAvatar: string
  isDarkTheme?: boolean
  isMobile?: boolean
}

const props = defineProps<Props>()

defineEmits<{
  'show-login': []
  'logout': []
  'go-to-profile': []
  'theme-toggle': []
  'show-guide': []
  'open-api-site': []
}>()

// 计算属性
const userRoleLabel = computed(() => {
  return props.userInfo?.role || '普通用户'
})

const quotaPercentage = computed(() => {
  if (props.quotaLimit === 0) return 0
  return Math.min((props.quotaUsed / props.quotaLimit) * 100, 100)
})
</script>

<style scoped>
.user-menu {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.user-info:hover {
  background-color: var(--el-fill-color-light);
}

.user-info:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: 2px;
}

.dropdown-arrow {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  transition: transform 0.3s ease;
}

.user-info:hover .dropdown-arrow {
  color: var(--el-text-color-primary);
}

.username {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.modern-user-dropdown {
  padding: 16px;
  min-width: 280px;
  background: var(--el-bg-color);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.user-profile-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.user-avatar-wrapper {
  position: relative;
}

.user-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: var(--el-color-success);
  border: 2px solid var(--el-bg-color);
  border-radius: 50%;
}

.user-basic-info {
  flex: 1;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-bottom: 4px;
}

.user-role-badge {
  font-size: 12px;
  color: var(--el-text-color-regular);
  background: var(--el-fill-color-light);
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.quota-card {
  background: var(--el-fill-color-lighter);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.quota-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.quota-icon {
  color: var(--el-color-warning);
}

.quota-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.quota-numbers {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
}

.quota-used {
  font-size: 18px;
  font-weight: 600;
  color: var(--el-color-primary);
}

.quota-separator {
  color: var(--el-text-color-regular);
}

.quota-total {
  font-size: 14px;
  color: var(--el-text-color-regular);
}

.quota-progress {
  height: 4px;
  background: var(--el-fill-color);
  border-radius: 2px;
  overflow: hidden;
}

.quota-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--el-color-primary), var(--el-color-primary-light-3));
  transition: width 0.3s ease;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-item:hover {
  background: var(--el-fill-color-light);
}

.action-item:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: 2px;
}

.action-item.logout-action:hover {
  background: var(--el-color-error-light-9);
  color: var(--el-color-error);
}

.action-item.version-item {
  cursor: default;
  opacity: 0.7;
}

.action-item.version-item:hover {
  background: transparent;
}

.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.action-text {
  font-size: 14px;
  font-weight: 500;
}

.menu-divider {
  height: 1px;
  background: var(--el-border-color-lighter);
  margin: 8px 0;
}

@media (max-width: 768px) {
  .modern-user-dropdown {
    min-width: 260px;
    padding: 12px;
  }
  
  .username {
    display: none;
  }
}
</style>
